import { errorView, formatClock, type QueryResultLike } from '@krp/client-runtime'
import type { PaymentVerdictVm, RecoveryLineState, RecoveryLineVm, RecoveryPanelVm, RecoveryPrimaryVm } from '@krp/sale-ui'

import type { CommandFeedback } from '../../../shared/model/feedback'
import { formatKopecks } from '../../../shared/model/format'
import type { AqsiRecoverAction, AqsiSaleState, AqsiSendableAction, AqsiStateLine } from './aqsiStateTypes'
import { manualPending } from './paymentFlow'
import type { AqsiSale, AqsiSaleOperation } from './types'

/**
 * Панель «Проверка в AQSI» (RP-CASH-RECOVERY §4.1, решение владельца 04.11.2026). Чистые функции: когда панель нужна, какие строки и
 * какое одно действие она показывает. Данные — снимок службы (`aqsiSaleState`), ход команды — итог цикла оплаты (`sale.recover`).
 */

export const PANEL_TITLE = 'Проверка в AQSI'
/** Подпись «Повторить оплату», если служба её не дала. */
export const RETRY_PAYMENT_LABEL = 'Повторить оплату'
/** Подпись основного действия чека, если служба её не дала (К-130). */
export const PRIMARY_FALLBACK_LABEL = 'Повторить пробитие'
/** Отказ `receipt/recover`: картина в AQSI изменилась, пока кассир смотрел на панель. */
export const STATE_CHANGED_CODE = 'state-changed'
export const STATE_CHANGED_TEXT = 'Состояние изменилось — проверьте и нажмите ещё раз'
/** Назначенное службой перечитывание — не чаще её кэша снимка (5 с на продажу). */
export const AQSI_STATE_RECHECK_MIN_MS = 5_000
const RECOVER_COMMAND = 'sale.recover'

/**
 * Внимание службы, при котором кассиру нужна проверка в AQSI (§5.1, «Тексты»): чек не фискализирован, ответ на чек потерян, касса занята.
 * Сюда же — чек с неизвестным исходом, которому служба назначила ручную сверку (её форма для чека заменена панелью).
 */
export const RECOVERY_ATTENTION_CODES: ReadonlySet<string> = new Set([
  'receipt-not-fiscalized',
  'receipt-lost-answer',
  'register-busy-ours',
  'register-busy-foreign',
  'register-busy-probe',
])

const LINE_LABEL: Readonly<Record<string, string>> = {
  terminal: 'Терминал (деньги)',
  receipt: 'Чек',
  register: 'Касса',
}

/** Слова службы о состоянии строки → знак панели. Незнакомое слово — «не знаем», текст службы остаётся. */
const LINE_STATE: Readonly<Record<string, RecoveryLineState>> = {
  ok: 'ok',
  yes: 'ok',
  done: 'ok',
  taken: 'ok',
  found: 'ok',
  free: 'ok',
  fiscalized: 'ok',
  succeeded: 'ok',
  completed: 'ok',
  problem: 'problem',
  no: 'problem',
  absent: 'problem',
  missing: 'problem',
  failed: 'problem',
  error: 'problem',
  refused: 'problem',
  'not-fiscalized': 'problem',
  busy: 'problem',
  'busy-ours': 'problem',
  'busy-foreign': 'problem',
  'busy-probe': 'problem',
  waiting: 'waiting',
  wait: 'waiting',
  pending: 'waiting',
  queued: 'waiting',
  processing: 'waiting',
  'in-progress': 'waiting',
  unknown: 'unknown',
}

/** Что окно говорит, пока основное действие в пути: честно «отправляем», а не «сделано». */
const SENDING_TEXT: Readonly<Record<AqsiSendableAction, string>> = {
  resend: 'Отправляем чек в AQSI с тем же слипом…',
  'close-found': 'Закрываем продажу найденным в AQSI чеком…',
  'cancel-stuck-then-resend': 'Снимаем зависшую команду чека и отправляем чек заново…',
  wait: 'Перечитываем состояние в AQSI…',
  'close-confirmed': 'Закрываем продажу: сверяем ваш ответ с чеками в AQSI…',
  'needs-human': 'Отправляем ваш ответ службе…',
  none: 'Перечитываем состояние в AQSI…',
}

export function recoverSendingText(action: AqsiSendableAction): string {
  return SENDING_TEXT[action]
}

const two = (value: number) => String(value).padStart(2, '0')

/** «14:05:12» — местное время чтения; `null` — момента нет или он не разобран. */
function clockWithSeconds(iso: string | null): string | null {
  if (iso === null) return null
  const value = Date.parse(iso)
  if (Number.isNaN(value)) return null
  const moment = new Date(value)
  return `${two(moment.getHours())}:${two(moment.getMinutes())}:${two(moment.getSeconds())}`
}

/** Касса занята НАШЕЙ незавершённой продажей (§4.3 п. 2, К-126): наша блокировка — нашими словами, никогда как ответ терминала. */
function isOurRegisterLock(line: AqsiStateLine, cell: string | null): boolean {
  return line.key === 'register' && (line.state === 'busy-ours' || cell === 'register-busy-ours')
}

export function ourRegisterLockText(amountKopecks: number | null): string {
  return amountKopecks === null
    ? 'Касса занята нашей незавершённой продажей — отложите её или довершите'
    : `Касса занята незавершённой продажей на ${formatKopecks(amountKopecks)} — отложите её или довершите`
}

function lineVm(line: AqsiStateLine, cell: string | null): RecoveryLineVm {
  const checked = clockWithSeconds(line.readAt)
  const ours = isOurRegisterLock(line, cell)
  return {
    key: line.key,
    label: LINE_LABEL[line.key] ?? line.key,
    state: ours ? 'problem' : (LINE_STATE[line.state] ?? 'unknown'),
    text: ours ? ourRegisterLockText(line.amountKopecks) : line.text,
    checkedText: checked === null ? null : `проверено ${checked}`,
  }
}

/**
 * Код основного действия, который окно может отправить службе в `receipt/recover`. `unknown` не отправляется никогда; `retry-payment` —
 * тоже: это новая оплата, её запускает путь «Картой» (К-130), чек для неё никто не пробивает.
 */
export function sendableAction(state: AqsiSaleState | undefined): AqsiSendableAction | null {
  const code = state?.primary?.code
  return code === undefined || code === 'unknown' || code === 'retry-payment' ? null : code
}

/** Основное действие панели — «Повторить оплату»: кнопка запускает оплату «Картой», а не `receipt/recover`. */
export function isRetryPayment(state: AqsiSaleState | undefined): boolean {
  return state?.primary?.code === 'retry-payment'
}

/** Действия, которые выполняет служба по кнопке; остальные коды — ожидание, подсказка или слово человека, кнопки у них нет. */
const BUTTON_ACTIONS: ReadonlySet<AqsiRecoverAction> = new Set(['resend', 'close-found', 'cancel-stuck-then-resend', 'retry-payment', 'unknown'])

/**
 * Нужна ли продаже панель «Проверка в AQSI»: деньги взяты, а исход чека не известен и служба назначила ручную сверку чека (раньше —
 * «Нужна ручная сверка»), либо служба сама назвала причину вниманием (§5.1). Операции оплаты (слип) остаются ручной сверке.
 */
export function needsAqsiCheck(sale: AqsiSale | null, operation: AqsiSaleOperation | undefined): boolean {
  if (sale === null || sale.isFinished || sale.isParked) return false
  const moneyIn = sale.paymentKind === 'Card' ? sale.payment === 'Succeeded' : sale.payment === 'NotRequired'
  if (!moneyIn) return false
  if (sale.attention.some((item) => RECOVERY_ATTENTION_CODES.has(item.code))) return true
  return sale.fiscal === 'Unknown' && operation !== undefined && operation.kind === 'receipt' && manualPending(operation)
}

export interface RecoveryPanelInput {
  state: QueryResultLike<AqsiSaleState> | undefined
  /** Чтение идёт (первое или повторное). */
  fetching: boolean
  /** Ход и итог последней команды цикла оплаты этой продажи. */
  cycle: CommandFeedback | null
  /** Продажу можно трогать командами: прочитана, своя касса, нет команды с неизвестным исходом. */
  canAct: boolean
  /**
   * Две галочки сверки чека (К-128, `receiptVerdictOf`), предзаполненные службой: показываются, только когда служба сама решить не может
   * (`needs-human`); `null` — операции чека для сверки нет.
   */
  verdict: PaymentVerdictVm | null
  /**
   * Можно ли начать новую оплату «Картой» (для «Повторить оплату», К-130) и почему нельзя — словами окна. Не передано — оплата не
   * ограничена (панели чека оплата не нужна).
   */
  payment?: { canPay: boolean; blockedReason: string | null }
}

function primaryVm(state: AqsiSaleState, sending: boolean, canAct: boolean, payment: RecoveryPanelInput['payment']): RecoveryPrimaryVm | null {
  const primary = state.primary
  if (primary === null || !BUTTON_ACTIONS.has(primary.code)) return null
  if (primary.code === 'unknown') {
    return {
      label: primary.label ?? PRIMARY_FALLBACK_LABEL,
      enabled: false,
      busy: false,
      blockedReason: `Служба предлагает действие «${primary.rawCode}», которого это окно не знает. Обновите страницу (F5) и проверьте ещё раз.`,
    }
  }
  if (primary.code === 'retry-payment') {
    // Новая оплата не ждёт «прошлой команды чека»: её допускает та же проверка, что кнопку «Картой» (`canPayNow`).
    const canPay = payment?.canPay ?? true
    const reason = !primary.enabled ? (primary.blockedReason ?? 'Служба пока не разрешает это действие — проверьте состояние ещё раз') : canPay ? null : (payment?.blockedReason ?? null)
    return { label: primary.label ?? RETRY_PAYMENT_LABEL, enabled: primary.enabled && canPay && !sending, busy: sending, blockedReason: reason }
  }
  const blockedReason = !primary.enabled
    ? (primary.blockedReason ?? 'Служба пока не разрешает это действие — проверьте состояние ещё раз')
    : !canAct
      ? 'Прошлая команда по этой продаже ещё не завершена — дождитесь её итога'
      : null
  return { label: primary.label ?? PRIMARY_FALLBACK_LABEL, enabled: primary.enabled && canAct && !sending, busy: sending, blockedReason }
}

/** «раз / раза / раз» для числа автопопыток. */
function timesText(count: number): string {
  const last = count % 10
  const tens = count % 100
  return last >= 2 && last <= 4 && (tens < 12 || tens > 14) ? `${count} раза` : `${count} раз`
}

/**
 * Что служба уже сделала сама (К-130): сколько раз повторила чек и когда следующая попытка; попытки кончились — честно просит нажать
 * кнопку. Пока служба ничего не повторяла, строки нет.
 */
export function autoAttemptsText(primary: AqsiSaleState['primary']): string | null {
  if (primary === null || primary.autoAttempts <= 0) return null
  const next = formatClock(primary.autoNextAt)
  return next === null
    ? 'Автопопытки исчерпаны — нажмите кнопку'
    : `Служба повторила сама ${timesText(primary.autoAttempts)} · следующая в ${next}`
}

/** Пояснение под строками: для ожидания — почему ждём (словами службы), для «ничего не делать» — что делать нечего. */
function explanationOf(state: AqsiSaleState): string[] {
  const primary = state.primary
  if (primary === null || primary.code === 'none') return ['Действий не требуется: служба доведёт продажу сама.']
  if (primary.code === 'wait' && primary.blockedReason !== null) return [primary.blockedReason]
  if (primary.code === 'needs-human') {
    return [primary.blockedReason ?? 'Служба не может решить сама. Отметьте, что видно на терминале и кассе, — галочки заполнены по данным AQSI.']
  }
  return []
}

/** Текст «Скопировать для разбора»: блок службы и то, что сейчас на экране (строки с временем, действие). */
function copyTextOf(state: AqsiSaleState | undefined, lines: readonly RecoveryLineVm[], primary: RecoveryPrimaryVm | null, errors: readonly string[]): string {
  return [
    PANEL_TITLE,
    ...lines.map((line) => `${line.label}: ${line.text}${line.checkedText === null ? '' : ` (${line.checkedText})`}`),
    ...(state?.cell != null ? [`Ячейка: ${state.cell}`] : []),
    ...(state?.primary != null ? [`Основное действие: ${state.primary.rawCode}${primary !== null ? ` — «${primary.label}»` : ''}`] : []),
    ...(primary?.blockedReason != null ? [primary.blockedReason] : []),
    ...errors,
    ...(state !== undefined && state.copyText !== '' ? ['', state.copyText] : []),
  ].join('\n')
}

/**
 * Панель для окна оплаты. Ход чтения называется честно: «Проверяем в AQSI…» до первого ответа, «Перечитываем…» поверх показанного;
 * отказ чтения — дословно и не исчезает; прочитанное раньше остаётся со своим временем.
 */
export function recoveryPanelVmOf(input: RecoveryPanelInput): RecoveryPanelVm {
  const state = input.state?.data
  const recover = input.cycle?.command === RECOVER_COMMAND ? input.cycle : null
  const sending = recover?.phase === 'sending'
  const error = input.state?.isError === true && input.state.error !== undefined ? errorView(input.state.error) : null
  const readError =
    error === null
      ? null
      : {
          tone: 'danger' as const,
          title: 'Не удалось проверить в AQSI',
          text: error.message,
          hint: state !== undefined ? 'Ниже — последнее, что известно, со временем проверки.' : null,
          meta: `Код: ${error.code}${error.correlationId === null ? '' : ` · запрос ${error.correlationId}`}`,
        }
  const progressText = sending
    ? (recover?.note ?? 'Отправляем команду…')
    : input.fetching
      ? state === undefined
        ? 'Проверяем в AQSI…'
        : 'Перечитываем состояние в AQSI…'
      : null
  const lines = state === undefined ? [] : state.lines.map((line) => lineVm(line, state.cell))
  const primary = state === undefined ? null : primaryVm(state, sending, input.canAct, input.payment)
  const nextCheck = state?.primary?.nextCheckAt ?? null
  const nextClock = formatClock(nextCheck)
  const stateChanged = recover?.phase === 'rejected' && recover.error?.code === STATE_CHANGED_CODE ? STATE_CHANGED_TEXT : null
  return {
    title: PANEL_TITLE,
    progressText,
    readError,
    lines,
    explanation: state === undefined ? [] : explanationOf(state),
    primary,
    nextCheckText: nextClock === null ? null : `Проверим снова в ${nextClock}`,
    autoNote: state === undefined ? null : autoAttemptsText(state.primary),
    stateChanged,
    canRefresh: !input.fetching && !sending,
    copyText: copyTextOf(state, lines, primary, [
      ...(error === null ? [] : [`Чтение: ${error.message} (код ${error.code}${error.correlationId === null ? '' : `, запрос ${error.correlationId}`})`]),
      ...(recover?.error == null
        ? []
        : [`Команда: ${recover.error.message} (код ${recover.error.code}${recover.error.correlationId === null ? '' : `, запрос ${recover.error.correlationId}`})`]),
    ]),
    verdict: state?.primary?.code === 'needs-human' ? input.verdict : null,
  }
}
